import { inheritRequestCorrelation } from '@brighthub/structured-logging';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ApiService,
  AwsApiWrapperService,
  GenericResponseCode,
  GenericResponseResult,
} from '@brighthub/api';
import {
  catchError,
  from,
  map,
  mergeMap,
  Observable,
  of,
  throwError,
} from 'rxjs';
import type { CreditOverviewApi } from '../credit-overview-api.token';
import { CREDIT_OVERVIEW_API_CONFIG } from '../config/credit-overview-api.config';
import type {
  CreditReportDocument,
  CreditReportSummaryPayload,
  CreditSubscriptionPayload,
} from '../credit-overview.model';
import {
  normalizeCreditReportResponse,
  normalizeCreditStatusResponse,
  normalizeCreditSummaryResponse,
} from './credit-overview-response.guard';

type Envelope<T> = GenericResponseResult & { payload?: T };

function envelopeErrorMessage(value: unknown): string | null {
  if (typeof value !== 'object' || value === null) return null;
  const result = (value as { result?: { message?: unknown } }).result;
  if (typeof result?.message === 'string' && result.message.trim()) {
    return result.message;
  }
  const payload = (value as { payload?: { message?: unknown } }).payload;
  return typeof payload?.message === 'string' && payload.message.trim()
    ? payload.message
    : null;
}

function emptyCreditSummary(): CreditReportSummaryPayload {
  return {
    reportAvailable: false,
    creditScore: null,
    debt: {
      summary: {
        totalDebt: '0',
        totalDebtAccounts: 0,
        aggregation: [],
      },
      accounts: [],
    },
  };
}

function isNotFoundEnvelope(raw: unknown): boolean {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return false;
  }
  const result = (raw as Record<string, unknown>)['result'];
  return (
    typeof result === 'object' &&
    result !== null &&
    !Array.isArray(result) &&
    (result as Record<string, unknown>)['status'] === 404
  );
}

@Injectable()
export class HttpCreditOverviewApiService implements CreditOverviewApi {
  private readonly _api = inject(ApiService);
  private readonly _wrapper = inject(AwsApiWrapperService);
  private readonly _apiName = CREDIT_OVERVIEW_API_CONFIG.apiName;
  private readonly _paths = CREDIT_OVERVIEW_API_CONFIG.paths;

  getStatus(guid: string): Observable<CreditSubscriptionPayload> {
    return from(
      this._wrapper.get(this._apiName, this._paths.status, {
        queryParams: { guid },
      }),
    ).pipe(map((raw) => normalizeCreditStatusResponse(raw, guid)));
  }

  getReportSummary(guid: string): Observable<CreditReportSummaryPayload> {
    return from(
      this._wrapper.get(this._apiName, this._paths.summary, {
        queryParams: { guid },
      }),
    ).pipe(
      map((raw) =>
        isNotFoundEnvelope(raw)
          ? emptyCreditSummary()
          : normalizeCreditSummaryResponse(raw),
      ),
      catchError((error: unknown) =>
        error instanceof HttpErrorResponse && error.status === 404
          ? of(emptyCreditSummary())
          : throwError(() => error),
      ),
    );
  }

  getReport(guid: string): Observable<CreditReportDocument> {
    return from(
      this._wrapper.get(
        this._apiName,
        this._paths.report,
        { queryParams: { guid } },
        'blob',
      ),
    ).pipe(mergeMap((raw) => from(normalizeCreditReportResponse(raw))));
  }

  inviteClient(guid: string): Observable<void> {
    return this._api
      .post<
        Envelope<unknown>
      >(this._apiName, this._paths.invite, { guid }, { suppressRequestBodyTelemetry: true })
      .pipe(
        map((response) => {
          if (
            response?.result?.status !== 200 ||
            response.result.code !== GenericResponseCode.SERVICE_OK
          ) {
            throw inheritRequestCorrelation(
              new Error(
                envelopeErrorMessage(response) ??
                  'Credit invite request failed',
              ),
              response,
            );
          }
        }),
        catchError((error: unknown) => {
          if (error instanceof HttpErrorResponse) {
            const message = envelopeErrorMessage(error.error);
            if (message)
              return throwError(() =>
                inheritRequestCorrelation(new Error(message), error),
              );
          }
          return throwError(() => error);
        }),
      );
  }
}
